import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import {
  type APIResponse,
  type ReportsMatterDemandReport,
  type ReportsMatterDemandReportResponse,
} from "@/lib/types";
import type { ReportDateRangeType } from "@/lib/utils/reports/report-filter-options";

export const DEMAND_TYPES = [
  "FIRST_PARTY",
  "THIRD_PARTY",
  "UNCLASSIFIED",
] as const;
export type DemandType = (typeof DEMAND_TYPES)[number];

const MAX_SEARCH_PAGES = 50;
const DEMAND_REPORT_PAGE_LIMIT = 250;

type MatterDemandReportDateRange = {
  endDate?: string;
  preset: ReportDateRangeType;
  startDate?: string;
};

export type RetrieveReportsMatterDemandReportInput = {
  demandTypes?: DemandType[];
  assigneeUserIds?: string[];
  dateRange: MatterDemandReportDateRange;
  incidentTypeIds?: string[];
  claimTypeIds?: string[];
  teamIds?: string[];
};

export const retrieveReportsMatterDemandReport = async (
  data: RetrieveReportsMatterDemandReportInput,
) => {
  let cursor: string | undefined;
  let pageCount = 0;
  let latestMeta: APIResponse<ReportsMatterDemandReportResponse>["meta"] = {
    userMsg: "",
  };
  let metrics: ReportsMatterDemandReportResponse["metrics"];
  const items: ReportsMatterDemandReport[] = [];

  do {
    const response = await authenticatedFastApiAxiosInstance.post<
      APIResponse<ReportsMatterDemandReportResponse>
    >(
      "reports/v2/matter-demands/query",
      {
        dateRange: data.dateRange,
        filters: {
          demandTypes: data.demandTypes ?? [],
          assigneeUserIds: data.assigneeUserIds ?? [],
          incidentTypeIds: data.incidentTypeIds ?? [],
          claimTypeIds: data.claimTypeIds ?? [],
          teamIds: data.teamIds ?? [],
        },
      },
      {
        params: {
          limit: DEMAND_REPORT_PAGE_LIMIT,
          ...(cursor ? { cursor } : {}),
        },
      },
    );
    const page = response.data;
    latestMeta = page.meta;
    metrics ??= page.data.metrics;
    items.push(...(page.data.rows ?? []));
    cursor =
      (page.pagination as { nextCursor?: string | null } | undefined)
        ?.nextCursor ?? undefined;
    pageCount += 1;
  } while (cursor && pageCount < MAX_SEARCH_PAGES);

  return {
    data: {
      rows: items,
      metrics,
    },
    meta: latestMeta,
  } satisfies APIResponse<ReportsMatterDemandReportResponse>;
};
